import { Switch, Route } from "wouter";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import LoginScreen from "@/components/LoginScreen";
import { AuthProvider, useAuth } from "@/lib/auth";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Laden...</span>
      </div>
    </div>
  );
}

// Lazy load the voice database builder component
const VoiceDatabaseBuilder = lazy(() => import("@/pages/voice-database-builder"));
// Lazy load the manual database builder component
const DatabaseBuilder = lazy(() => import("@/pages/database-builder"));
// Lazy load the excel import component
const ExcelImport = lazy(() => import("@/pages/excel-import"));
// Lazy load the Excel LV import component (LV + Urkalkulation, bestätigte Spalten-Zuordnung)
const ExcelLvImport = lazy(() => import("@/pages/excel-lv-import"));
// Lazy load the GAEB import component
const GAEBImport = lazy(() => import("@/pages/gaeb-import"));
// Lazy load the PDF LV import component
const PdfLvImport = lazy(() => import("@/pages/pdf-lv-import"));
// Lazy load the IFC/BIM import component (3D-Viewer + Mengenübernahme)
const IfcImport = lazy(() => import("@/pages/ifc-import"));
// Lazy load the voice quote builder component (experimental)
const VoiceQuoteBuilder = lazy(() => import("@/pages/voice-quote-builder"));
// Lazy load the settings page
const Settings = lazy(() => import("@/pages/settings"));
// Lazy load the invoices page (Rechnungswesen)
const Invoices = lazy(() => import("@/pages/invoices"));
// Lazy load the Leistungsstand page (cumulative billing matrix)
const Leistungsstand = lazy(() => import("@/pages/leistungsstand"));

function AppContent() {
  const { isAuthenticated, login, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={login} />;
  }

  // Show main app if authenticated
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/voice-database-builder">
        <Suspense fallback={<LoadingFallback />}>
          <VoiceDatabaseBuilder />
        </Suspense>
      </Route>
      <Route path="/database-builder">
        <Suspense fallback={<LoadingFallback />}>
          <DatabaseBuilder />
        </Suspense>
      </Route>
      <Route path="/excel-import">
        <Suspense fallback={<LoadingFallback />}>
          <ExcelImport />
        </Suspense>
      </Route>
      <Route path="/excel-lv-import">
        <Suspense fallback={<LoadingFallback />}>
          <ExcelLvImport />
        </Suspense>
      </Route>
      <Route path="/gaeb-import">
        <Suspense fallback={<LoadingFallback />}>
          <GAEBImport />
        </Suspense>
      </Route>
      <Route path="/pdf-lv-import">
        <Suspense fallback={<LoadingFallback />}>
          <PdfLvImport />
        </Suspense>
      </Route>
      <Route path="/ifc-import">
        <Suspense fallback={<LoadingFallback />}>
          <IfcImport />
        </Suspense>
      </Route>
      <Route path="/voice-quote-builder">
        <Suspense fallback={<LoadingFallback />}>
          <VoiceQuoteBuilder />
        </Suspense>
      </Route>
      <Route path="/invoices">
        <Suspense fallback={<LoadingFallback />}>
          <Invoices />
        </Suspense>
      </Route>
      <Route path="/leistungsstand">
        <Suspense fallback={<LoadingFallback />}>
          <Leistungsstand />
        </Suspense>
      </Route>
      <Route path="/settings">
        <Suspense fallback={<LoadingFallback />}>
          <Settings />
        </Suspense>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
